import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons/faExternalLink';
import { faThumbsDown as faThumbsDownOutline } from '@fortawesome/pro-regular-svg-icons/faThumbsDown';
import { faThumbsUp as faThumbsUpOutline } from '@fortawesome/pro-regular-svg-icons/faThumbsUp';
import { faThumbsDown as faThumbsDownSolid } from '@fortawesome/pro-solid-svg-icons/faThumbsDown';
import { faThumbsUp as faThumbsUpSolid } from '@fortawesome/pro-solid-svg-icons/faThumbsUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DOMPurify from 'dompurify';
import he from 'he';
import { useTranslation } from 'react-i18next';

import Button, { ButtonTypes } from 'components/common/button';
import ModalCard from 'components/common/modal_card';
import PopoverCard from 'components/common/popover_card';
import SafeHtml from 'components/common/safe_html';
import Tooltip from 'components/common/tooltip';
import TeamAssistAnswerPreviewModal from 'components/customer/team_assist/team_assist_answer_preview_modal';
import PortalModalWrapper from 'components/lib/portal_modal_wrapper';
import TeamAssistCitation from 'models/team_assist_citation';
import TeamAssistMessage from 'models/team_assist_message';
import escapeHtml from 'scripts/lib/escape_html';

// onRate receives `{ value, reason?, comment? }`, or null to clear the rating.
export function ThumbsFeedback({ onRate, ratingValue }) {
  const { t } = useTranslation();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const isThumbsUp = ratingValue === TeamAssistMessage.RatingValue.thumbs_up;
  const isThumbsDown = ratingValue === TeamAssistMessage.RatingValue.thumbs_down;

  const handleThumbsUp = () => {
    onRate(isThumbsUp ? null : { value: TeamAssistMessage.RatingValue.thumbs_up });
  };

  const handleThumbsDown = () => {
    if (isThumbsDown) {
      onRate(null);
    } else {
      setShowFeedbackModal(true);
    }
  };

  const handleFeedbackSubmit = ({ reason, comment }) => {
    const rating = { value: TeamAssistMessage.RatingValue.thumbs_down };
    if (reason) rating.reason = reason;
    if (comment) rating.comment = comment;
    onRate(rating);
    setShowFeedbackModal(false);
  };

  // Once rated, only the chosen thumb stays on screen — the rating reads as a
  // settled state rather than a still-open question. Clicking it again clears
  // the rating and brings both back.
  return (
    <>
      {!isThumbsDown && (
        <Tooltip delay={250} message={t('Good response')} position="bottom" style={{ padding: '5px 10px' }}>
          <ThumbButton
            aria-label={isThumbsUp ? t('Remove thumbs up') : t('Thumbs up')}
            data-aid="teamAssistThumbsUpButton"
            isActive={isThumbsUp}
            onClick={handleThumbsUp}
            type="button"
          >
            <FontAwesomeIcon icon={isThumbsUp ? faThumbsUpSolid : faThumbsUpOutline} />
          </ThumbButton>
        </Tooltip>
      )}
      {!isThumbsUp && (
        <Tooltip delay={250} message={t('Bad response')} position="bottom" style={{ padding: '5px 10px' }}>
          <ThumbButton
            aria-label={isThumbsDown ? t('Remove thumbs down') : t('Thumbs down')}
            data-aid="teamAssistThumbsDownButton"
            isActive={isThumbsDown}
            onClick={handleThumbsDown}
            type="button"
          >
            <FontAwesomeIcon icon={isThumbsDown ? faThumbsDownSolid : faThumbsDownOutline} />
          </ThumbButton>
        </Tooltip>
      )}
      {showFeedbackModal && (
        <TeamAssistFeedbackModal onClose={() => setShowFeedbackModal(false)} onSubmit={handleFeedbackSubmit} />
      )}
    </>
  );
}

ThumbsFeedback.propTypes = {
  onRate: PropTypes.func.isRequired,
  ratingValue: PropTypes.oneOf(Object.values(TeamAssistMessage.RatingValue)),
};

// Resolves false when there is nothing to copy.
export function copyAnswerToClipboard(html) {
  const plainText = getPlainTextFromHtml(html);
  const richText = html || '';
  if (!plainText && !richText.trim()) return Promise.resolve(false);

  const item = {
    'text/plain': new Blob([plainText], { type: 'text/plain' }),
    'text/html': new Blob([richText], { type: 'text/html' }),
  };
  // Starting from a resolved promise routes a missing ClipboardItem, which
  // throws, into the plain-text fallback too.
  return Promise.resolve()
    .then(() => navigator.clipboard.write([new window.ClipboardItem(item)]))
    .catch(() => navigator.clipboard.writeText(plainText))
    .then(() => true);
}

export const citationType = PropTypes.instanceOf(TeamAssistCitation);

export function CannedResponsePrompt({ hasError, label, prompt }) {
  const { t } = useTranslation();
  const { isOpen, popoverProps, setOpenerElement, toggle } = usePopoverToggle();

  return (
    <>
      <Tooltip
        delay={250}
        message={isOpen ? null : t('See the prompt this sent')}
        position="left"
        style={{ padding: '5px 10px' }}
      >
        <ClickableUserBubble
          aria-expanded={isOpen}
          data-aid="teamAssistCannedResponseMarker"
          hasError={hasError}
          onClick={toggle}
          ref={setOpenerElement}
        >
          <SafeHtml html={label} />
          {/* Decorative: the button's own text is its accessible name, and
              aria-expanded already conveys that it opens something. */}
          <PromptMarkerIcon aria-hidden="true" icon={faBolt} />
        </ClickableUserBubble>
      </Tooltip>
      <PromptPopover autoPosition data-aid="teamAssistCannedResponsePrompt" position="left" {...popoverProps}>
        <PromptHeading>
          <PromptMarkerIcon aria-hidden="true" icon={faBolt} />
          {t('Quick response')}
        </PromptHeading>
        <PromptSubheading>{t('This is the prompt that was sent:')}</PromptSubheading>
        <PromptBody>{prompt}</PromptBody>
      </PromptPopover>
    </>
  );
}

CannedResponsePrompt.propTypes = {
  hasError: PropTypes.bool,
  label: PropTypes.string,
  prompt: PropTypes.string,
};

function feedbackReasons(t) {
  return [
    { value: TeamAssistMessage.RatingReason.incorrect_or_incomplete, label: t('Incorrect or incomplete') },
    { value: TeamAssistMessage.RatingReason.slow_or_buggy, label: t('Slow or buggy') },
    { value: TeamAssistMessage.RatingReason.style_or_tone, label: t('Style or tone') },
    { value: TeamAssistMessage.RatingReason.other, label: t('Other') },
  ];
}

function TeamAssistFeedbackModal({ onClose, onSubmit }) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState(null);
  const [comment, setComment] = useState('');

  return (
    <PortalModalWrapper>
      <FeedbackModalOverlay>
        <ModalCard data-aid="teamAssistFeedbackModal" onClose={onClose}>
          <FeedbackModalContent>
            <FeedbackModalTitle>{t('What went wrong?')}</FeedbackModalTitle>
            <FeedbackReasonButtons>
              {feedbackReasons(t).map(({ value, label }) => (
                <FeedbackReasonButton
                  data-aid="teamAssistFeedbackReasonButton"
                  isSelected={selectedReason === value}
                  key={value}
                  onClick={() => setSelectedReason(selectedReason === value ? null : value)}
                  type="button"
                >
                  {label}
                </FeedbackReasonButton>
              ))}
            </FeedbackReasonButtons>
            <FeedbackCommentArea
              data-aid="teamAssistFeedbackComment"
              onChange={e => setComment(e.target.value)}
              placeholder={t('Share details (optional)')}
              rows={3}
              value={comment}
            />
            <FeedbackModalActions>
              <Button buttonType={ButtonTypes.TEXT} data-aid="teamAssistFeedbackCancel" onClick={onClose}>
                {t('Cancel')}
              </Button>
              <Button
                buttonType={ButtonTypes.PRIMARY}
                data-aid="teamAssistFeedbackSubmit"
                isSmall
                onClick={() => handleSubmit({ comment, onSubmit, selectedReason })}
              >
                {t('Submit')}
              </Button>
            </FeedbackModalActions>
          </FeedbackModalContent>
        </ModalCard>
      </FeedbackModalOverlay>
    </PortalModalWrapper>
  );
}

function handleSubmit({ comment, onSubmit, selectedReason }) {
  onSubmit({ reason: selectedReason, comment: comment.trim() || null });
}

TeamAssistFeedbackModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export function ReviewReasons({ reviewReasons }) {
  const { t } = useTranslation();
  if (!reviewReasons || reviewReasons.length === 0) {
    return null;
  }

  return (
    <ReviewReasonsContainer>
      <ReviewReasonsIcon className="fa fa-exclamation-triangle" />
      <ReviewReasonsText>
        <ReviewReasonsLabel>{t('Needs review')}</ReviewReasonsLabel>
        {reviewReasons.map((reason, idx) => (
          <ReviewReason key={idx}>{reason}</ReviewReason>
        ))}
      </ReviewReasonsText>
    </ReviewReasonsContainer>
  );
}

ReviewReasons.propTypes = {
  reviewReasons: PropTypes.arrayOf(PropTypes.string),
};

export function Citations({ citations, onCitationClick, onClosePreview, previewAnswerId }) {
  const { t } = useTranslation();
  if (!citations || citations.length === 0) {
    return null;
  }

  return (
    <>
      <CitationsDivider />
      <CitationsLabel>{t('Sources')}</CitationsLabel>
      <CitationsList>
        {citations.map((citation, idx) => (
          <Citation
            citation={citation}
            key={citationKey(citation, idx)}
            number={idx + 1}
            onAnswerClick={onCitationClick}
          />
        ))}
      </CitationsList>
      {previewAnswerId && <TeamAssistAnswerPreviewModal onClose={onClosePreview} />}
    </>
  );
}

Citations.propTypes = {
  citations: PropTypes.arrayOf(citationType),
  onCitationClick: PropTypes.func.isRequired,
  onClosePreview: PropTypes.func.isRequired,
  previewAnswerId: PropTypes.string,
};

// Checked in this order, so a web page carrying both a URL and a passage links
// out. The fallthrough matters: an unknown source type still gets a label.
function Citation({ citation, number, onAnswerClick }) {
  const { t } = useTranslation();
  const label = citation.getLabel();
  const marker = <CitationNumber>[{number}]</CitationNumber>;
  const inertLabel = (
    <CitationText>
      {marker} {label}
    </CitationText>
  );

  if (citation.isAnswer()) {
    if (!citation.snippetId) {
      return inertLabel;
    }
    return (
      <CitationLink
        aria-label={t('Open referenced answer {{label}}', { label })}
        data-aid="teamAssistCitationAnswerLink"
        href="#"
        onClick={evt => {
          evt.preventDefault();
          onAnswerClick(citation.snippetId);
        }}
      >
        {marker} {label}
      </CitationLink>
    );
  }

  // A URL that fails the scheme check falls through to the passage or the inert
  // label, which is the right degradation — better a citation you can't open
  // than an href that runs in the agent's session.
  if (citation.hasSafeUrl()) {
    return (
      <CitationLink
        aria-label={t('Open referenced source {{label}} in a new tab', { label })}
        data-aid="teamAssistCitationSourceLink"
        href={citation.url}
        rel="noopener noreferrer nofollow"
        target="_blank"
      >
        {marker} {label} <CitationLinkIcon aria-hidden="true" icon={faExternalLink} />
      </CitationLink>
    );
  }

  if (citation.hasPassage()) {
    return <CitationPassage citation={citation} label={label} number={number} />;
  }

  return inertLabel;
}

Citation.propTypes = {
  citation: citationType.isRequired,
  number: PropTypes.number.isRequired,
  onAnswerClick: PropTypes.func.isRequired,
};

function CitationPassage({ citation, label, number }) {
  const { t } = useTranslation();
  const { isOpen, popoverProps, setOpenerElement, toggle } = usePopoverToggle();

  return (
    <>
      <CitationPassageButton
        aria-expanded={isOpen}
        aria-label={t('Show the cited passage from {{label}}', { label })}
        data-aid="teamAssistCitationPassageToggle"
        onClick={toggle}
        ref={setOpenerElement}
      >
        <CitationNumber>[{number}]</CitationNumber> {label}
      </CitationPassageButton>
      <CitationPassagePopover autoPosition data-aid="teamAssistCitationPassage" position="top" {...popoverProps}>
        <CitationPassageHeading>{label}</CitationPassageHeading>
        {citation.location ? <CitationPassageLocation>{citation.location}</CitationPassageLocation> : null}
        {citation.excerpt ? <CitationPassageBody>{citation.excerpt}</CitationPassageBody> : null}
      </CitationPassagePopover>
    </>
  );
}

CitationPassage.propTypes = {
  citation: citationType.isRequired,
  label: PropTypes.string,
  number: PropTypes.number.isRequired,
};

// Content-derived where the citation offers something to derive from: a
// knowledge-source citation carries no id on the wire, so its URL or label
// stands in. Position still leads, because two passages from one document can
// share a label and duplicate keys are a worse failure than unstable ones.
function citationKey(citation, idx) {
  return `${idx}-${citation.snippetId || citation.url || citation.getLabel()}`;
}

function getPlainTextFromHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = DOMPurify.sanitize(html);
  return div.textContent || div.innerText || '';
}

// Returns unsanitized HTML: render it through SafeHtml.
export function formatMessageContent(content) {
  if (!content) {
    return '';
  }
  if (containsHtml(content)) {
    return content;
  }
  // Decode first so already-encoded plain text (e.g. "Crate &amp; Barrel" from the
  // backend) isn't double-encoded by escapeHtml into "&amp;amp;". Idempotent for
  // literal input. See safe_html.jsx — the browser decodes one level on render.
  return escapeHtml(he.decode(content)).replace(/\r\n|\r|\n/g, '<br />');
}

// Note: may false-positive on plain text with angle brackets (e.g. "x < y and z > 0"),
// but this is unlikely in practice since message content is either plain text or HTML from the backend.
function containsHtml(content) {
  return /<\/?[a-z][\s\S]*>/i.test(content);
}

function usePopoverToggle() {
  const [isOpen, setIsOpen] = useState(false);
  const [openerElement, setOpenerElement] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = evt => {
      if (evt.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  return {
    isOpen,
    setOpenerElement,
    toggle: () => setIsOpen(open => !open),
    popoverProps: {
      bounds: {
        bottom: window.innerHeight - POPOVER_VIEWPORT_INSET,
        left: POPOVER_VIEWPORT_INSET,
        right: window.innerWidth - POPOVER_VIEWPORT_INSET,
        top: POPOVER_VIEWPORT_INSET,
      },
      isVisible: isOpen,
      // Ignoring the opener is what makes the toggle work: it sits outside the
      // portalled popover, so its own mousedown reaches this handler and would
      // close the popover an instant before its onClick reopened it.
      onClickOutside: evt => {
        if (openerElement && openerElement.contains(evt.target)) {
          return;
        }
        setIsOpen(false);
      },
      targetElement: openerElement,
    },
  };
}

export function replaceCitationRefsInContent(html, citations) {
  if (!html || !citations || citations.length === 0) return html;

  let result = html;
  citations.forEach((citation, idx) => {
    // Only Answers are referenced by id in the prose; a knowledge-source
    // reference never reaches the client. Still indexed off the full list so
    // these numbers agree with the rail's.
    if (!citation.snippetId) return;

    const num = idx + 1;
    const escapedId = citation.snippetId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const sup = `<sup class="cite-ref">[${num}]</sup>`;
    // Match patterns like (snippetId – Name) or (snippetId - Name), optionally wrapped in <em>/<i> tags
    const pattern = new RegExp(`(?:<(?:em|i)>)?\\(?${escapedId}(?:\\s*[–—-]\\s*[^)<]*)?\\)?(?:</(?:em|i)>)?`, 'g');
    result = result.replace(pattern, sup);
  });
  return result;
}

export const UserBubbleSpacer = styled.div`
  flex-shrink: 0;
  width: 22px;
`;

export const UserRow = styled.div`
  align-self: flex-end;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
  max-width: 100%;
  width: 100%;
`;

export const AssistantRow = styled.div`
  align-self: flex-start;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
  max-width: 100%;
  width: 100%;
`;

export const AssistantMessageWrapper = styled.div`
  align-items: stretch;
  display: flex;
  max-width: 100%;
  width: 100%;
`;

export const CopyButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  flex-shrink: 0;
  font-size: var(--font-size-medium);
  height: 16px;
  line-height: 1;
  margin-top: 2px;
  padding: 0;
  width: 16px;

  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

export const UserBubble = styled.div`
  align-items: center;
  background: ${p => (p.hasError ? p.theme.colors.red100 : p.theme.colors.gray100)};
  border-radius: 12px 2px 12px 12px;
  color: ${p => p.theme.colors.black};
  display: flex;
  font-size: ${p => p.theme.fontSize.base};
  gap: 8px;
  line-height: 1.4;
  max-width: 100%;
  overflow-wrap: break-word;
  padding: 8px 12px;
  word-break: break-word;

  > div {
    min-width: 0;
  }
`;

const ClickableUserBubble = styled(UserBubble).attrs({ as: 'button', type: 'button' })`
  border: 1px solid ${p => p.theme.colors.purple400};
  cursor: pointer;
  font-family: inherit;
  margin: 0;
  text-align: left;

  &:hover {
    background: ${p => (p.hasError ? p.theme.colors.red200 : p.theme.colors.gray200)};
  }
`;

const PromptMarkerIcon = styled(FontAwesomeIcon)`
  color: ${p => p.theme.colors.purple400};
  flex-shrink: 0;
  font-size: ${p => p.theme.fontSize.reduced};
`;

const PromptPopover = styled(PopoverCard)`
  max-width: 340px;
  width: 340px;
`;

const PromptHeading = styled.div`
  align-items: center;
  color: ${p => p.theme.colors.black};
  display: flex;
  font-size: ${p => p.theme.fontSize.base};
  font-weight: ${p => p.theme.fontWeight.heavy};
  gap: 6px;
`;

const PromptSubheading = styled.div`
  color: ${p => p.theme.colors.gray700};
  font-size: ${p => p.theme.fontSize.reduced};
  margin-bottom: 8px;
`;

const PromptBody = styled.div`
  color: ${p => p.theme.colors.gray800};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.45;
  max-height: 260px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const ErrorRow = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  margin-top: 4px;
`;

export const ErrorIcon = styled.i`
  color: ${p => p.theme.colors.red400};
  font-size: var(--font-size-reduced);
`;

export const ErrorText = styled.span`
  color: ${p => p.theme.colors.red400};
  font-size: var(--font-size-reduced);
`;

export const ErrorRetryButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.red400};
  cursor: pointer;
  font-size: var(--font-size-reduced);
  padding: 0 0 0 4px;

  &:hover {
    color: ${p => p.theme.colors.red300};
    text-decoration: underline;
  }
`;

export const AssistantContent = styled.div`
  color: ${p => p.theme.colors.black};
  flex: 1;
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  min-width: 0;
  word-wrap: break-word;

  .teamAssistImageVerificationTable {
    border-collapse: collapse;
    margin: 8px 0;

    tr:hover {
      background-color: ${p => p.theme.colors.gray100};
    }
    th,
    td {
      padding: 4px;
    }
    th {
      padding-right: 8px;
      text-align: right;
      vertical-align: top;
    }
  }

  .teamAssistImageVerificationRecommendation {
    border: 1px solid ${p => p.theme.colors.gray300};
    border-radius: 8px;
    margin: 8px 0;
    padding: 8px;
  }
`;

export const AssistantActions = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  margin-top: 8px;
`;

export const UserMessageContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-wrap: break-word;
  padding-bottom: 16px;
`;

export const SuggestedResponseContent = styled.div`
  p {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  ul,
  ol {
    margin: 4px 0;
    padding-left: 20px;
  }
  sup.cite-ref {
    color: ${p => p.theme.colors.purple400};
    font-size: var(--font-size-small);
    font-weight: 600;
  }
`;

export const SuggestedResponseActions = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  padding-top: 8px;
`;

export const SuggestedResponseCopy = styled.div`
  display: flex;
`;

export const UseButton = styled(Button)`
  min-width: 64px;
`;

export const RetryIconButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  flex-shrink: 0;
  font-size: var(--font-size-medium);
  height: 16px;
  line-height: 1;
  margin-top: 1px;
  padding: 0;
  width: 16px;

  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

// Unselected matches the sibling copy/retry buttons exactly: gray500, gray700
// on hover, no background. Selecting adds the pill and pins the icon at the
// hovered colour so the choice stays visible once the pointer leaves.
//
// The pill is a translucent mid-gray rather than a gray-ramp token because the
// ramp inverts between themes — any token light enough to sit under the light
// surface resolves darker than the dark-theme surface and disappears. A mid
// gray at low alpha darkens a light surface and lightens a dark one.
const ThumbButton = styled.button`
  align-items: center;
  background: ${p => (p.isActive ? 'rgba(128, 128, 128, 0.15)' : 'none')};
  border: none;
  border-radius: 4px;
  color: ${p => (p.isActive ? p.theme.colors.gray700 : p.theme.colors.gray500)};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-size: var(--font-size-medium);
  height: 22px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 22px;

  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

const ReviewReasonsContainer = styled.div`
  align-items: flex-start;
  background: ${p => p.theme.colors.yellow100};
  border-radius: 4px;
  display: flex;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 8px;
`;

const ReviewReasonsIcon = styled.i`
  color: ${p => p.theme.colors.yellow600};
  flex-shrink: 0;
  font-size: var(--font-size-reduced);
  margin-top: 2px;
`;

const ReviewReasonsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ReviewReasonsLabel = styled.span`
  color: ${p => p.theme.colors.yellow700};
  font-size: var(--font-size-reduced);
  font-weight: 600;
`;

const ReviewReason = styled.span`
  color: ${p => p.theme.colors.yellow600};
  font-size: var(--font-size-reduced);
  line-height: 1.3;
`;

const CitationsDivider = styled.hr`
  border: none;
  border-top: 1px solid ${p => p.theme.colors.gray300};
  height: 0;
  margin: 12px 0 8px 0;
`;

const CitationsLabel = styled.span`
  color: ${p => p.theme.colors.gray800};
  font-size: var(--font-size-reduced);
  margin-bottom: 4px;
`;

const CitationsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-size: var(--font-size-reduced);
  gap: 4px 12px;
  line-height: 1.3;
`;

const CitationNumber = styled.span`
  font-weight: 600;
`;

const CitationLink = styled.a`
  color: ${p => p.theme.colors.green400};
  text-decoration: none;

  &:hover {
    color: ${p => p.theme.colors.green300};
  }
`;

const CitationLinkIcon = styled(FontAwesomeIcon)`
  font-size: var(--font-size-small);
`;

const CitationText = styled.span`
  color: ${p => p.theme.colors.gray800};
`;

// Dotted rather than the links' plain treatment: it reveals, it doesn't navigate.
const CitationPassageButton = styled.button`
  background: none;
  border: none;
  border-bottom: 1px dotted ${p => p.theme.colors.green400};
  color: ${p => p.theme.colors.green400};
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  padding: 0;
  text-align: left;

  &:hover {
    border-bottom-color: ${p => p.theme.colors.green300};
    color: ${p => p.theme.colors.green300};
  }
`;

const CitationPassagePopover = styled(PopoverCard)`
  max-width: 340px;
  width: 340px;
`;

const CitationPassageHeading = styled.div`
  color: ${p => p.theme.colors.black};
  font-size: ${p => p.theme.fontSize.base};
  font-weight: ${p => p.theme.fontWeight.heavy};
  word-break: break-word;
`;

const CitationPassageLocation = styled.div`
  color: ${p => p.theme.colors.gray700};
  font-size: ${p => p.theme.fontSize.reduced};
`;

const CitationPassageBody = styled.div`
  color: ${p => p.theme.colors.gray800};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.45;
  margin-top: 8px;
  max-height: 260px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const FeedbackModalOverlay = styled.div`
  .modalCard-content {
    max-width: 400px;
    padding: 20px;
    width: 400px;
  }
`;

const FeedbackModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeedbackModalTitle = styled.h3`
  color: ${p => p.theme.colors.black};
  font-size: ${p => p.theme.fontSize.large};
  font-weight: 600;
  margin: 0;
`;

const FeedbackReasonButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FeedbackReasonButton = styled.button`
  background: ${p => (p.isSelected ? p.theme.colors.green400 : p.theme.colors.gray100)};
  border: 1px solid ${p => (p.isSelected ? p.theme.colors.green400 : p.theme.colors.gray300)};
  border-radius: 16px;
  color: ${p => (p.isSelected ? '#fff' : p.theme.colors.gray800)};
  cursor: pointer;
  font-size: var(--font-size-base);
  padding: 6px 14px;
  transition: all 0.15s ease;

  &:hover {
    background: ${p => (p.isSelected ? p.theme.colors.green300 : p.theme.colors.gray200)};
    border-color: ${p => (p.isSelected ? p.theme.colors.green300 : p.theme.colors.gray400)};
  }
`;

const FeedbackCommentArea = styled.textarea`
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 6px;
  color: ${p => p.theme.colors.black};
  font-family: inherit;
  font-size: var(--font-size-base);
  padding: 8px 10px;
  resize: vertical;

  &:focus {
    border-color: ${p => p.theme.colors.green400};
    outline: none;
  }

  &::placeholder {
    color: ${p => p.theme.colors.gray500};
  }
`;

const FeedbackModalActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const POPOVER_VIEWPORT_INSET = 10;
